import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Toolbar,
  Alert,
  CircularProgress,
  Stack,
  Pagination,
  Grid,
  FormControlLabel,
  Switch,
  Tooltip,
  Avatar,
} from "@mui/material";
import {
  Search,
  Edit,
  Refresh,
  SupervisorAccount,
  Person,
  AdminPanelSettings,
  Block,
  CheckCircle,
  Cancel,
} from "@mui/icons-material";

interface User {
  id: number;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  date_joined?: string | null;
  last_login?: string | null;
}

interface UserStats {
  total_users: number;
  active_users: number;
  inactive_users: number;
  staff_users: number;
  superusers: number;
}

interface BulkAction {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: "primary" | "secondary" | "error" | "warning" | "success";
}

const bulkActions: BulkAction[] = [
  {
    id: "activate",
    label: "Activate Users",
    icon: <CheckCircle />,
    color: "success",
  },
  {
    id: "deactivate",
    label: "Deactivate Users",
    icon: <Cancel />,
    color: "error",
  },
  {
    id: "make_staff",
    label: "Make Staff",
    icon: <AdminPanelSettings />,
    color: "primary",
  },
  {
    id: "remove_staff",
    label: "Remove Staff",
    icon: <Person />,
    color: "warning",
  },
];

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);

  // Search and filtering
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [roleFilter, setRoleFilter] = useState<
    "all" | "staff" | "superuser" | "regular"
  >("all");

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 20;

  // Dialogs
  const [bulkActionDialog, setBulkActionDialog] = useState(false);
  const [selectedBulkAction, setSelectedBulkAction] = useState<string>("");
  const [editUserDialog, setEditUserDialog] = useState<User | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        search: searchQuery,
        ordering: "-date_joined",
      });

      // Add status filter
      if (statusFilter === "active") {
        params.append("is_active", "true");
      } else if (statusFilter === "inactive") {
        params.append("is_active", "false");
      }

      // Add role filter
      if (roleFilter === "staff") {
        params.append("is_staff", "true");
      } else if (roleFilter === "superuser") {
        params.append("is_superuser", "true");
      } else if (roleFilter === "regular") {
        params.append("is_staff", "false");
        params.append("is_superuser", "false");
      }

      const response = await fetch(`/users/admin/users/?${params}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setUsers(data.results || data);

      // Handle pagination if present
      if (data.count) {
        setTotalPages(Math.ceil(data.count / pageSize));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch users");
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, statusFilter, roleFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch("/users/admin/users/stats/");
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setStats(data);
    } catch {
      // Error handled silently for lint compliance
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, [fetchUsers, fetchStats]);

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const handleBulkAction = async () => {
    if (!selectedBulkAction || selectedUsers.length === 0) return;

    setLoading(true);
    try {
      const response = await fetch("/users/admin/users/bulk-actions/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCsrfToken(),
        },
        body: JSON.stringify({
          user_ids: selectedUsers,
          action: selectedBulkAction,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      await response.json(); // Response processed but data not needed
      setSelectedUsers([]);
      setBulkActionDialog(false);
      fetchUsers();
      fetchStats();

      // Show success message
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Bulk action failed");
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async (user: User, updates: Partial<User>) => {
    setLoading(true);
    try {
      const response = await fetch(`/users/admin/users/${user.id}/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCsrfToken(),
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      setEditUserDialog(null);
      fetchUsers();
      fetchStats();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update user");
    } finally {
      setLoading(false);
    }
  };

  const toggleUserSelection = (userId: number) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const toggleAllUsers = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map((user) => user.id));
    }
  };

  const getCsrfToken = (): string => {
    const cookieValue = document.cookie
      .split("; ")
      .find((row) => row.startsWith("csrftoken="))
      ?.split("=")[1];
    return cookieValue || "";
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Not provided';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'Not provided';
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getUserInitials = (user: User) => {
    return (
      `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`.toUpperCase() ||
      user.email[0].toUpperCase()
    );
  };

  const getRoleChips = (user: User) => {
    const chips = [];
    if (user.is_superuser) {
      chips.push(
        <Chip
          key="super"
          size="small"
          color="error"
          label="Super"
          icon={<SupervisorAccount />}
        />,
      );
    } else if (user.is_staff) {
      chips.push(
        <Chip
          key="staff"
          size="small"
          color="primary"
          label="Staff"
          icon={<AdminPanelSettings />}
        />,
      );
    } else {
      chips.push(
        <Chip
          key="user"
          size="small"
          color="default"
          label="User"
          icon={<Person />}
        />,
      );
    }
    return chips;
  };

  return (
    <Box sx={{ p: { xs: 0, sm: 3 }, minWidth: 0 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          User Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage users, permissions, and access across your application
        </Typography>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Users
                </Typography>
                <Typography variant="h4">{stats.total_users}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Active
                </Typography>
                <Typography variant="h4" color="statusText.ready">
                  {stats.active_users}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Inactive
                </Typography>
                <Typography variant="h4" color="statusText.attention">
                  {stats.inactive_users}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Staff
                </Typography>
                <Typography variant="h4" color="readable.primary">
                  {stats.staff_users}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Superusers
                </Typography>
                <Typography variant="h4" color="statusText.inFlight">
                  {stats.superusers}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            component="form"
            onSubmit={handleSearch}
            sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}
          >
            <TextField
              label="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: <Search sx={{ mr: 1 }} />,
              }}
              sx={{ flex: '1 1 240px', minWidth: 0 }}
            />

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) =>
                  setStatusFilter(
                    e.target.value as "all" | "active" | "inactive",
                  )
                }
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={roleFilter}
                label="Role"
                onChange={(e) =>
                  setRoleFilter(
                    e.target.value as "all" | "staff" | "superuser" | "regular",
                  )
                }
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="regular">Regular</MenuItem>
                <MenuItem value="staff">Staff</MenuItem>
                <MenuItem value="superuser">Superuser</MenuItem>
              </Select>
            </FormControl>

            <Button type="submit" variant="contained" startIcon={<Search />}>
              Search
            </Button>

            <Button onClick={fetchUsers} startIcon={<Refresh />}>
              Refresh
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Bulk Actions Toolbar */}
      {selectedUsers.length > 0 && (
        <Card sx={{ mb: 2 }}>
          <Toolbar sx={{ flexWrap: 'wrap', gap: 2, py: 1 }}>
            <Typography variant="subtitle1" sx={{ flex: 1 }}>
              {selectedUsers.length} user{selectedUsers.length !== 1 ? "s" : ""}{" "}
              selected
            </Typography>
            <Button
              variant="contained"
              onClick={() => setBulkActionDialog(true)}
              startIcon={<Edit />}
            >
              Bulk Actions
            </Button>
          </Toolbar>
        </Card>
      )}

      {/* Users Table */}
      <Card>
        <TableContainer role="region" aria-label="User management" tabIndex={0}>
          <Table aria-label="Users" sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={
                      selectedUsers.length === users.length && users.length > 0
                    }
                    indeterminate={
                      selectedUsers.length > 0 &&
                      selectedUsers.length < users.length
                    }
                    onChange={toggleAllUsers}
                  />
                </TableCell>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No users found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => toggleUserSelection(user.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}
                      >
                        <Avatar sx={{ width: 32, height: 32, flexShrink: 0 }}>
                          {getUserInitials(user)}
                        </Avatar>
                        <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                          <Typography variant="body2" fontWeight="medium">
                            {user.first_name} {user.last_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {getRoleChips(user)}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={user.is_active ? "Active" : "Inactive"}
                        color={user.is_active ? "success" : "error"}
                        icon={user.is_active ? <CheckCircle /> : <Block />}
                      />
                    </TableCell>
                    <TableCell>{formatDate(user.date_joined)}</TableCell>
                    <TableCell>
                      {user.last_login === null ? 'Never' : formatDate(user.last_login)}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit User">
                        <IconButton
                          size="small"
                          onClick={() => setEditUserDialog(user)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2, overflowX: 'auto' }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, newPage) => setPage(newPage)}
              color="primary"
            />
          </Box>
        )}
      </Card>

      {/* Bulk Action Dialog */}
      <Dialog
        open={bulkActionDialog}
        onClose={() => setBulkActionDialog(false)}
      >
        <DialogTitle>Bulk Action</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Select an action to perform on {selectedUsers.length} selected user
            {selectedUsers.length !== 1 ? "s" : ""}:
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Action</InputLabel>
            <Select
              value={selectedBulkAction}
              label="Action"
              onChange={(e) => setSelectedBulkAction(e.target.value)}
            >
              {bulkActions.map((action) => (
                <MenuItem key={action.id} value={action.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {action.icon}
                    {action.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkActionDialog(false)}>Cancel</Button>
          <Button
            onClick={handleBulkAction}
            variant="contained"
            disabled={!selectedBulkAction || loading}
          >
            {loading ? <CircularProgress size={20} /> : "Apply"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      {editUserDialog && (
        <Dialog
          open={true}
          onClose={() => setEditUserDialog(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit User: {editUserDialog.email}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editUserDialog.is_active}
                    onChange={(e) =>
                      setEditUserDialog({
                        ...editUserDialog,
                        is_active: e.target.checked,
                      })
                    }
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editUserDialog.is_staff}
                    onChange={(e) =>
                      setEditUserDialog({
                        ...editUserDialog,
                        is_staff: e.target.checked,
                      })
                    }
                  />
                }
                label="Staff"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editUserDialog.is_superuser}
                    onChange={(e) =>
                      setEditUserDialog({
                        ...editUserDialog,
                        is_superuser: e.target.checked,
                      })
                    }
                  />
                }
                label="Superuser"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditUserDialog(null)}>Cancel</Button>
            <Button
              onClick={() =>
                handleEditUser(editUserDialog, {
                  is_active: editUserDialog.is_active,
                  is_staff: editUserDialog.is_staff,
                  is_superuser: editUserDialog.is_superuser,
                })
              }
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default UserManagement;
